import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import AdvertiserAvatar from "../../elements/AdvertiserAvatar";
import AdvertiserCardCell from "./AdvertiserCardCell";
import AdvertiserRowActionsMenu from "./AdvertiserRowActionsMenu";
import StatusBadge from "../../elements/StatusBadge";
import TrendIndicator from "../../elements/TrendIndicator";
import { AdvertiserRowData } from "./advertiserUtils";
import { AdvertiserType } from "../../../../types/advertiserTypes";

type AdvertisersTableProps = {
  rows: AdvertiserRowData[];
  advertisersById: Map<string, AdvertiserType>;
  onRowClick: (id: string) => void;
  onEditPaymentMethod: (advertiser: AdvertiserType) => void;
  onHubspotConnect: (advertiser: AdvertiserType) => void;
  onHubspotDisconnect: (advertiser: AdvertiserType) => void;
  onArchive: (advertiser: AdvertiserType) => void;
  onUnarchive: (advertiser: AdvertiserType) => void;
  showArchived: boolean;
};

const TableWrap = styled("div")({
  marginBottom: 8,
});

const TableBox = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  borderRadius: 10,
  border: `1px solid ${theme.palette.brand.gray200}`,
  overflowX: "auto",
}));

const Table = styled("table")({
  width: "100%",
  minWidth: 900,
  borderCollapse: "collapse",
  fontSize: 13,
});

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  "& th": {
    padding: "12px 16px",
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.brand.gray500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: theme.palette.brand.gray50,
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
    textAlign: "left",
  },
  "& th:first-child": {
    position: "sticky",
    left: 0,
    zIndex: 2,
    boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
  },
  "& th.alignRight": {
    textAlign: "right",
  },
}));

const Tbody = styled("tbody")(({ theme }: { theme: Theme }) => ({
  "& tr": {
    borderBottom: `1px solid ${theme.palette.brand.gray100}`,
    cursor: "pointer",
    transition: "background 0.15s",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: theme.palette.brand.gray50,
    },
  },
  "& td": {
    padding: "14px 16px",
    color: theme.palette.brand.gray700,
  },
  "& td:first-child": {
    position: "sticky",
    left: 0,
    zIndex: 1,
    background: theme.palette.brand.white,
    boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
  },
  "& tr:hover td:first-child": {
    background: theme.palette.brand.gray50,
  },
  "& td.alignRight": {
    textAlign: "right",
  },
}));

const AdvertiserCell = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

const NameText = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const NumericPrimary = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const NumericBold = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 700,
  color: theme.palette.brand.gray900,
}));

const SecondaryText = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
}));

const HubspotCell = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  alignItems: "flex-start",
});

const HubspotAccountId = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brand.green,
}));

const ActionsCell = styled("div")({
  display: "inline-flex",
  justifyContent: "flex-end",
  width: "100%",
});

const ArchivedBadge = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  padding: "2px 6px",
  borderRadius: 4,
  background: theme.palette.brand.gray100,
  color: theme.palette.brand.gray500,
  marginLeft: 6,
}));

const AdvertisersTable: FC<AdvertisersTableProps> = ({
  rows,
  advertisersById,
  onRowClick,
  onEditPaymentMethod,
  onHubspotConnect,
  onHubspotDisconnect,
  onArchive,
  onUnarchive,
  showArchived,
}) => (
  <TableWrap>
    <TableBox>
      <Table>
        <Thead>
          <tr>
            <th>Advertiser</th>
            <th>Domain</th>
            <th>HubSpot</th>
            <th>Card</th>
            <th className="alignRight">Campaigns</th>
            <th className="alignRight">Spend (MTD)</th>
            <th className="alignRight">Impressions</th>
            <th className="alignRight">CTR</th>
            <th className="alignRight" aria-label="Actions" />
          </tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <tr key={row.id} onClick={() => onRowClick(row.id)}>
              <td>
                <AdvertiserCell>
                  <AdvertiserAvatar name={row.name} />
                  <NameText>{row.name}</NameText>
                  {advertisersById.get(row.id)?.archived ? <ArchivedBadge>Archived</ArchivedBadge> : null}
                </AdvertiserCell>
              </td>
              <td>
                <SecondaryText>{row.domain}</SecondaryText>
              </td>
              <td>
                <HubspotCell>
                  <StatusBadge
                    status={row.hubspotConnected ? "Connected" : "Not connected"}
                    variant={row.hubspotConnected ? "active" : "draft"}
                  />
                  {row.hubspotConnected && row.hubspotPortalId ? (
                    <HubspotAccountId>ID: {row.hubspotPortalId}</HubspotAccountId>
                  ) : null}
                </HubspotCell>
              </td>
              <td>
                <AdvertiserCardCell card={row.card} />
              </td>
              <td className="alignRight">
                <NumericPrimary>{row.campaignCount}</NumericPrimary>
              </td>
              <td className="alignRight">
                <NumericBold>{row.spendMtd}</NumericBold>
              </td>
              <td className="alignRight">
                <SecondaryText>{row.impressions}</SecondaryText>
              </td>
              <td className="alignRight">
                <TrendIndicator direction={row.ctrTrend} label={row.ctr} />
              </td>
              <td className="alignRight">
                <ActionsCell onMouseDown={(event) => event.stopPropagation()}>
                  {advertisersById.get(row.id) ? (
                    <AdvertiserRowActionsMenu
                      advertiser={advertisersById.get(row.id)!}
                      onEditPaymentMethod={onEditPaymentMethod}
                      onHubspotConnect={onHubspotConnect}
                      onHubspotDisconnect={onHubspotDisconnect}
                      onArchive={onArchive}
                      onUnarchive={onUnarchive}
                    />
                  ) : null}
                </ActionsCell>
              </td>
            </tr>
          ))}
        </Tbody>
      </Table>
    </TableBox>
  </TableWrap>
);

export default AdvertisersTable;
